/**
 * An invite code, held across the sign-in round trip.
 *
 * The second guardian arrives at `/join?code=…` without a session. Signing in
 * is a magic link, and a magic link comes back to the origin — not to the page
 * that asked for it — so the code has to survive a trip through the mail and
 * land somewhere the app will look afterwards. That is all this file is.
 *
 * `localStorage`, reluctantly, and not `sessionStorage`: the mail client hands
 * the link to the browser, which frequently opens it in a NEW tab, and a new
 * tab is a new session store. Stashing in `sessionStorage` would work on the
 * happy path and silently lose the invite in the common one — the invited
 * guardian would land on `/`, be sent to /welcome, and be offered the chance to
 * start a second household instead of joining the one that invited them. That
 * is a worse failure than the one `sessionStorage` was protecting against.
 *
 * What that protection was for is kept, though, by a short shelf life instead
 * of a tab boundary. A code is a live key to a household, and one lying around
 * a shared family tablet is exactly what the seven-day expiry and the
 * single-use flag exist to bound; a stash is only ever meant to outlive a walk
 * to the inbox, so anything older than that is treated as absent. The code is
 * also cleared the moment /join reads it, whether or not redemption succeeds.
 *
 * Every accessor is wrapped, because a browser in private mode can throw on
 * the storage object itself rather than merely returning nothing. A remembered
 * code is a convenience; failing to remember one costs the user a paste, not
 * their invite.
 */

const KEY = 'fortunaut.pending-invite';

/** Long enough to check your email, short enough not to be left behind. */
const SHELF_LIFE_MS = 2 * 60 * 60 * 1000;

/** The shape the database will mint — see 0005's check constraint. */
const CODE = /^[0-9a-f]{32}$/;

export function normalizeInviteCode(raw) {
  const code = String(raw ?? '').trim().toLowerCase();
  return CODE.test(code) ? code : null;
}

/**
 * Stash a code for after sign-in. Junk is dropped rather than stored: a
 * malformed code that survived would be retried on every screen the app
 * renders, which turns a mistyped link into an app that will not load.
 */
export function rememberInviteCode(raw) {
  const code = normalizeInviteCode(raw);
  if (!code) return null;
  try {
    window.localStorage.setItem(KEY, JSON.stringify({ code, at: Date.now() }));
  } catch { /* a stash is a convenience, not a requirement */ }
  return code;
}

export function pendingInviteCode() {
  let stored;
  try { stored = window.localStorage.getItem(KEY); } catch { return null; }
  if (!stored) return null;

  let parsed;
  try { parsed = JSON.parse(stored); } catch { forgetInviteCode(); return null; }

  const code = normalizeInviteCode(parsed?.code);
  const fresh = Number.isFinite(parsed?.at) && Date.now() - parsed.at < SHELF_LIFE_MS;
  if (!code || !fresh) { forgetInviteCode(); return null; }
  return code;
}

export function forgetInviteCode() {
  try { window.localStorage.removeItem(KEY); } catch { /* nothing to do */ }
}

/** The link a guardian hands over. */
export function inviteLink(code, origin = window.location.origin) {
  return `${origin}/join?code=${encodeURIComponent(code)}`;
}
